import { ChangeEvent, useEffect, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { FONT_OPTIONS } from "@constants/font-options";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUITheme } from "@iddataweb/idw-common-ui/types";
import { SelectOption } from "@idw-types/common";
import { loadFont } from "@iddataweb/idw-common-ui/utils";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { PageLevelTypes } from "@enums/page-level-types";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { AXNUIThemePolicyRetryMessage } from "@iddataweb/idw-common-ui/types/axnui-theme/axnui-them-policy-retry-message";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ThemingPolicyRetryMessageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";


type ThemingPolicyRetryMessageStyleSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiTheme: AXNUITheme;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingPolicyRetryMessageValidationErrors;
    updateAXNUIThemePolicyRetryPropertyValue: (key: keyof AXNUIThemePolicyRetryMessage, value: string) => void;
};

export const ThemingPolicyRetryMessageStyleSection = (props: ThemingPolicyRetryMessageStyleSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiTheme,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIThemePolicyRetryPropertyValue
    } = props;

    useEffect(() => {
        if (axnuiTheme.policyRetryMessage.font) {
            loadFont(axnuiTheme.policyRetryMessage.font, "axnui-policy-retry-message-font");
        }
    }, [axnuiTheme.policyRetryMessage.font]);

    const handleRetryMessageBackgroundColor = (newValue: string) => {
        updateAXNUIThemePolicyRetryPropertyValue("backgroundColor", newValue);
    };

    const handleResetRetryMessageBackgroundColor = () => {
        updateAXNUIThemePolicyRetryPropertyValue("backgroundColor", inheritedAXNUITheme.policyRetryMessage.backgroundColor);
    };
    
    const handleRetryMessageBorderColor = (newValue: string) => {
        updateAXNUIThemePolicyRetryPropertyValue("borderColor", newValue);
    };

    const handleResetRetryMessageBorderColor = () => {
        updateAXNUIThemePolicyRetryPropertyValue("borderColor", inheritedAXNUITheme.policyRetryMessage.backgroundColor);
    };
    const handleRetryMessageBorderWidth = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIThemePolicyRetryPropertyValue("borderWidth", event.target.value);
    };

    const handleResetRetryMessageBorderWidth = () => {
        updateAXNUIThemePolicyRetryPropertyValue("borderWidth", inheritedAXNUITheme.policyRetryMessage.borderWidth);
    };

    const handleRetryMessageCornerRadius = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIThemePolicyRetryPropertyValue("cornerRadius", event.target.value);
    };

    const handleResetRetryMessageCornerRadius = () => {
        updateAXNUIThemePolicyRetryPropertyValue("cornerRadius", inheritedAXNUITheme.policyRetryMessage.cornerRadius);
    };

    const handleFontChange = (newValue: SelectOption) => {
        updateAXNUIThemePolicyRetryPropertyValue("font", newValue.label);
    };

    const handleResetFont = () => {
        updateAXNUIThemePolicyRetryPropertyValue("font", inheritedAXNUITheme.policyRetryMessage.font);
    };
    const handleFontColorChange = (newValue: string) => {
        updateAXNUIThemePolicyRetryPropertyValue("fontColor", newValue);
    };

    const handleResetFontColor = () => {
        updateAXNUIThemePolicyRetryPropertyValue("fontColor", inheritedAXNUITheme.policyRetryMessage.fontColor);
    };

    const selectedFontOption = useMemo(() => {
        if (axnuiTheme.policyRetryMessage.font) {
            return FONT_OPTIONS.find(fo => fo.label === axnuiTheme.policyRetryMessage.font);
        }
        return null;
    }, [axnuiTheme.policyRetryMessage.font]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.policyRetryMessage.font}
                currentThemingPropertyValue={axnuiTheme.policyRetryMessage.font}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-policy-retry-message-font-select-input"}
                    automationId={"theming-policy-retry-message-font-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.font.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.font.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFontChange}
                    options={FONT_OPTIONS}
                    selectedOption={selectedFontOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.font.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFont}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.policyRetryMessage.fontColor}
                currentThemingPropertyValue={axnuiTheme.policyRetryMessage.fontColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-policy-retry-message-font-color-picker"}
                    automationId={"theming-policy-retry-message-font-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.fontColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.fontColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.policyRetryMessage.fontColor || ""}
                    onChange={handleFontColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.inputs.fontColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFontColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.policyRetryMessage.backgroundColor}
                currentThemingPropertyValue={axnuiTheme.policyRetryMessage.backgroundColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-policy-retry-message-background-color-picker"}
                    automationId={"theming-policy-retry-message-retry-message-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.backgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.backgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.policyRetryMessage.backgroundColor || ""}
                    onChange={handleRetryMessageBackgroundColor}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.backgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetRetryMessageBackgroundColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.policyRetryMessage.borderColor}
                currentThemingPropertyValue={axnuiTheme.policyRetryMessage.borderColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-policy-retry-message-border-color-picker"}
                    automationId={"theming-policy-retry-message-retry-message-border-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.policyRetryMessage.borderColor || ""}
                    onChange={handleRetryMessageBorderColor}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetRetryMessageBorderColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.policyRetryMessage.cornerRadius}
                currentThemingPropertyValue={axnuiTheme.policyRetryMessage.cornerRadius}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-policy-retry-message-border-radius-input"}
                    automationId={"theming-policy-retry-message-border-radius-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderRadius.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderRadius.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleRetryMessageCornerRadius}
                    value={axnuiTheme.policyRetryMessage.cornerRadius || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderRadius.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetRetryMessageCornerRadius}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.borderRadiusError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.policyRetryMessage.borderWidth}
                currentThemingPropertyValue={axnuiTheme.policyRetryMessage.borderWidth}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-policy-retry-message-border-width"}
                    automationId={"theming-policy-retry-message-border-width"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderWidth.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderWidth.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleRetryMessageBorderWidth}
                    value={axnuiTheme.policyRetryMessage.borderWidth || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.borderWidth.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetRetryMessageBorderWidth}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.borderWidthError}
            
        </div>
    );
};